import { useState, useEffect, useRef } from 'react';

type PopupStep = 'form' | 'success';

function ExitIntentModal({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<PopupStep>('form');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [timeLeft, setTimeLeft] = useState(600); // 10 min countdown

  // Countdown timer
  useEffect(() => {
    if (step !== 'form') return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [step]);

  const minutes = String(Math.floor(timeLeft / 60)).padStart(2, '0');
  const seconds = String(timeLeft % 60).padStart(2, '0');
  const isExpired = timeLeft === 0;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    const form = e.currentTarget;
    const nameVal = (form.elements.namedItem('name') as HTMLInputElement)?.value.trim();
    const phoneVal = (form.elements.namedItem('phone') as HTMLInputElement)?.value.trim();
    const emailVal = (form.elements.namedItem('email') as HTMLInputElement)?.value.trim();

    if (!nameVal || !phoneVal || !emailVal) {
      setFormError('Preencha todos os campos para garantir sua condição.');
      return;
    }

    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: nameVal,
        phone: phoneVal,
        email: emailVal,
        origem: 'Exit Intent Popup',
      });

      await fetch('https://readdy.ai/api/form/d727g50hb0rqicn68trg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const msg = encodeURIComponent(
        `Olá! Vi a oferta exclusiva no site e quero garantir minha consultoria gratuita.\n\nMeu nome é ${nameVal} e gostaria de saber mais sobre a taxa reduzida especial para consórcio.`
      );
      setStep('success');
      setTimeout(() => {
        window.open(`https://wa.me/5511922305730?text=${msg}`, '_blank');
      }, 900);
    } catch {
      setFormError('Erro ao enviar. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Oferta especial de saída"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" onClick={onClose} />

      {/* Card */}
      <div className="relative bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl animate-exit-popup">

        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 transition-colors cursor-pointer"
          aria-label="Fechar"
        >
          <i className="ri-close-line text-white text-lg" />
        </button>

        {/* ── STEP: FORM ── */}
        {step === 'form' && (
          <>
            {/* Hero banner */}
            <div className="relative bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500 px-8 pt-8 pb-7 overflow-hidden">
              {/* decorative circles */}
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10" />
              <div className="absolute -bottom-4 -left-6 w-24 h-24 rounded-full bg-white/10" />

              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-yellow-400 text-yellow-900 text-xs font-black px-3 py-1 rounded-full uppercase tracking-wide">
                    Oferta exclusiva
                  </span>
                  {/* Timer */}
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${isExpired ? 'bg-red-500/80 text-white' : 'bg-white/20 text-white'}`}>
                    <i className="ri-timer-line mr-1" />
                    {isExpired ? 'Expirada' : `${minutes}:${seconds}`}
                  </span>
                </div>

                <p className="text-white/80 text-sm font-medium mb-1">Espera! Antes de sair...</p>
                <h2 className="text-white text-2xl font-black leading-tight mb-2">
                  Consultoria gratuita +{' '}
                  <span className="text-yellow-300">taxa reduzida</span>
                  <br />só para você
                </h2>
                <p className="text-white/75 text-sm leading-relaxed">
                  Oferta exclusiva para visitantes que saem sem simular. Válida apenas enquanto o timer estiver ativo.
                </p>
              </div>
            </div>

            {/* Benefits strip */}
            <div className="flex items-stretch border-b border-gray-100">
              {[
                { icon: 'ri-user-star-line', label: 'Consultor\ndedicado', color: 'text-teal-500' },
                { icon: 'ri-percent-line', label: 'Taxa\nreduzida', color: 'text-emerald-600' },
                { icon: 'ri-flashlight-line', label: 'Resposta\nem 5 min', color: 'text-teal-600' },
              ].map((b, i) => (
                <div
                  key={i}
                  className={`flex-1 flex flex-col items-center justify-center py-3.5 px-2 text-center ${i < 2 ? 'border-r border-gray-100' : ''}`}
                >
                  <div className={`w-7 h-7 flex items-center justify-center mb-1 ${b.color}`}>
                    <i className={`${b.icon} text-xl`} />
                  </div>
                  <span className="text-xs text-gray-600 font-semibold leading-tight whitespace-pre-line">{b.label}</span>
                </div>
              ))}
            </div>

            {/* Form */}
            <div className="px-7 py-6">
              <p className="text-sm text-gray-500 mb-5 text-center">
                Informe seus dados e garanta a condição especial agora:
              </p>

              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-3"
              >
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-line text-gray-400 text-sm" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    autoComplete="name"
                    placeholder="Seu nome completo"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-teal-400 focus:outline-none transition-colors placeholder:text-gray-400"
                  />
                </div>

                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-gray-400 text-sm" />
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    placeholder="WhatsApp (com DDD)"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-teal-400 focus:outline-none transition-colors placeholder:text-gray-400"
                  />
                </div>

                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line text-gray-400 text-sm" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="Seu melhor e-mail"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-teal-400 focus:outline-none transition-colors placeholder:text-gray-400"
                  />
                </div>

                {formError && (
                  <p className="text-xs text-red-500 flex items-center gap-1.5">
                    <i className="ri-error-warning-line" />
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting || isExpired}
                  className="w-full flex items-center justify-center gap-2.5 bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3.5 rounded-xl text-sm transition-colors whitespace-nowrap cursor-pointer mt-1"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-whatsapp-line text-lg" />
                      Garantir minha condição especial
                    </>
                  )}
                </button>
              </form>

              <button
                type="button"
                onClick={onClose}
                className="w-full mt-3 text-gray-400 hover:text-gray-500 text-xs py-1.5 transition-colors cursor-pointer"
              >
                Não, obrigado. Prefiro pagar mais caro.
              </button>

              <p className="text-center text-xs text-gray-400 flex items-center justify-center gap-1 mt-3">
                <i className="ri-shield-check-line" />
                Seus dados são 100% seguros. Sem spam.
              </p>
            </div>
          </>
        )}

        {/* ── STEP: SUCCESS ── */}
        {step === 'success' && (
          <div className="px-8 py-12 text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-5">
              <i className="ri-check-double-line text-4xl text-green-600" />
            </div>
            <h3 className="text-xl font-black text-gray-900 mb-2">
              Condição especial garantida!
            </h3>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed max-w-xs mx-auto">
              Abrindo o WhatsApp agora. Um consultor dedicado vai atender você com a taxa reduzida exclusiva.
            </p>
            <div className="bg-teal-50 border border-teal-100 rounded-2xl p-4 mb-6">
              <p className="text-xs text-teal-700 font-semibold uppercase tracking-widest mb-1">Seus benefícios garantidos</p>
              <ul className="space-y-1.5 text-sm text-teal-800">
                <li className="flex items-center gap-2"><i className="ri-check-line text-teal-500" /> Consultoria gratuita personalizada</li>
                <li className="flex items-center gap-2"><i className="ri-check-line text-teal-500" /> Taxa administrativa reduzida</li>
                <li className="flex items-center gap-2"><i className="ri-check-line text-teal-500" /> Atendimento prioritário</li>
              </ul>
            </div>
            <button
              onClick={onClose}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors cursor-pointer underline underline-offset-2"
            >
              Fechar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ExitIntentPopup() {
  const [show, setShow] = useState(false);
  const triggered = useRef(false);

  useEffect(() => {
    // Inject animation keyframes
    const styleId = 'exit-popup-keyframes';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes exit-popup-in {
          from { opacity: 0; transform: scale(0.88) translateY(24px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-exit-popup {
          animation: exit-popup-in 0.38s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `;
      document.head.appendChild(style);
    }

    const SESSION_KEY = 'exit_popup_shown';
    if (sessionStorage.getItem(SESSION_KEY)) return;

    // Desktop: mouse leaves viewport through the top
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 8 && !triggered.current) {
        triggered.current = true;
        sessionStorage.setItem(SESSION_KEY, '1');
        setShow(true);
      }
    };

    // Mobile fallback: show after 45s of inactivity on page
    const mobileTimer = setTimeout(() => {
      if (!triggered.current) {
        triggered.current = true;
        sessionStorage.setItem(SESSION_KEY, '1');
        setShow(true);
      }
    }, 45000);

    // Scroll-back-up intent on mobile (user scrolls up fast near top)
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const currentY = window.scrollY;
      const scrolledUp = lastScrollY - currentY;
      if (scrolledUp > 80 && currentY < 300 && !triggered.current) {
        triggered.current = true;
        sessionStorage.setItem(SESSION_KEY, '1');
        setShow(true);
      }
      lastScrollY = currentY;
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(mobileTimer);
    };
  }, []);

  if (!show) return null;
  return <ExitIntentModal onClose={() => setShow(false)} />;
}
